/* eslint-disable no-console */
/* eslint-disable prefer-destructuring */

import extend from './extend';

export default class Menu {
    /**
     * Creates an instance of Menu.
     * @param {string} button The DOM element Menu instance is added to.
     * @param {object} options Instance options
     * @memberof Menu
     */
    constructor(button, options) {
        this.html = document.getElementsByTagName('html')[0];

        // fail safe check
        if (!button || button.nodeType !== Node.ELEMENT_NODE) {
            console.warn('DOM element is missing, please correct. Menu is unable to initialize.');
            return;
        }

        this.button = button;
        this.hasOpenMenu = false;
        this.optionsCustom = options || {};

        // default options
        this.optionsDefault = {
            focus: null,
        };

        // merge default + user defined options
        this.settings = extend(this.optionsDefault, this.optionsCustom);

        // add initial button semantics
        this.button.setAttribute('aria-haspopup', true);
        this.button.setAttribute('aria-expanded', false);

        // get the menu associated with button
        this.menuId = this.button.getAttribute('aria-controls');
        this.menu = document.getElementById(this.menuId);

        // exit if menu doesn't exist
        if (!this.menu || this.menu.nodeType !== Node.ELEMENT_NODE) {
            console.warn(`Element #${this.menuId} is not found. Make sure you're calling the correct menu ID.`);
            return;
        }

        // hide menu initially
        this.menu.hidden = true;
        this.menu.setAttribute('aria-hidden', true);

        // if focus target is assigned, get first child
        if (this.settings.focus) {
            this.focus = document.querySelectorAll(this.settings.focus)[0];
        }

        // handle button click on button to show/hide menu
        this.button.addEventListener('click', this.toggle.bind(this));

        // keyboard listeners
        this.button.addEventListener('keydown', (e) => {
            // open menu on down arrow
            if (e.keyCode === 40) {
                if (this.menu.hidden) this.open();
            }

            // close menu on up arrow & ESC key
            if (e.keyCode === 38 || e.keyCode === 27) {
                this.close();
            }
        });

        // close all open with ESC
        this.closeAllOpen = (e) => {
            if (this.hasOpenMenu) {
                if (e.keyCode === 27) {
                    this.close();
                    this.button.focus();
                }
            }
        };

        document.addEventListener('keydown', this.closeAllOpen, false);

        // initiate listeners object for public events
        this.listeners = {};
    }

    /**
     * Method to open menu.
     * @memberof Menu
     */
    open() {
        this.button.setAttribute('aria-expanded', true);
        this.menu.setAttribute('aria-hidden', false);
        this.menu.hidden = false;
        this.hasOpenMenu = true;

        const outsideClick = (e) => {
            if (!this.menu.contains(e.target) && !this.button.contains(e.target)) {
                this.close();
                document.removeEventListener('click', outsideClick);
            }
        };

        document.addEventListener('click', outsideClick);

        setTimeout(() => {
            this.html.setAttribute('data-hasoverlay', true);
        }, 50);

        if (this.focus) this.focus.focus();
        this.fire('open', this);

        return this;
    }

    /**
     * Method to close menu
     * @memberof Menu
     */
    close() {
        this.html.setAttribute('data-hasoverlay', false);
        this.button.setAttribute('aria-expanded', false);
        this.menu.setAttribute('aria-hidden', true);
        this.menu.hidden = true;
        this.hasOpenMenu = false;

        this.fire('close');

        return this;
    }

    /**
     * Checks whether current button is expanded or not.
     * @returns If current button is expanded, close method will fire, otherwise open method will fire.
     * @memberof Menu
     */
    toggle() {
        const expanded = this.button.getAttribute('aria-expanded') === 'true';
        return expanded ? this.close() : this.open();
    }

    /**
     * Subscribe to events.
     * @param {string} type Method type you are subscribing to.
     * @param {function} handler Do something when type is subscribed to.
     * @memberof Menu
     */
    on(type, handler) {
        if (typeof this.listeners[type] === 'undefined') {
            this.listeners[type] = [];
        }

        this.listeners[type].push(handler);

        return this;
    }

    fire(type, data) {
        const listeners = this.listeners[type] || [];

        listeners.forEach((listener) => {
            listener(data);
        });
    }
}
